import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { MdClose, MdUpload, MdCrop, MdCheck, MdWarning, MdImage, MdZoomIn, MdZoomOut } from 'react-icons/md';

// Constants for image restrictions
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MIN_DIMENSION = 200; // Minimum 200px
const MAX_DIMENSION = 2000; // Maximum 2000px
const OUTPUT_WIDTH = 400; // Output square width
const OUTPUT_HEIGHT = 400; // Output square height (1:1 aspect ratio)
const ASPECT_RATIO = 1; // Square aspect ratio

function ProfilePictureCropper({ onUpload, onClose, currentPictureUrl }) {
  const [, setSelectedFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [step, setStep] = useState('select'); // 'select', 'crop', 'preview'
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scale, setScale] = useState(1);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    // Check file type
    if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/)) {
      return 'Please select a JPG, PNG, or WebP image file.';
    }
    
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `Image is too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`;
    }
    
    return null;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSelectedFile(file);

    // Read the file
    const reader = new FileReader();
    reader.onload = () => {
      // Validate image dimensions
      const img = new Image();
      img.onload = () => {
        if (img.width < MIN_DIMENSION || img.height < MIN_DIMENSION) {
          setError(`Image is too small. Minimum dimensions are ${MIN_DIMENSION}x${MIN_DIMENSION} pixels.`);
          return;
        }
        if (img.width > MAX_DIMENSION || img.height > MAX_DIMENSION) {
          setError(`Image is too large. Maximum dimensions are ${MAX_DIMENSION}x${MAX_DIMENSION} pixels.`);
          return;
        }
        setImageSrc(reader.result);
        setStep('crop');
        
        // Set initial crop maintaining 4:5 aspect ratio
        const cropHeight = Math.min(img.height * 0.8, img.width * 0.8 / ASPECT_RATIO);
        const cropWidth = cropHeight * ASPECT_RATIO;
        
        setCrop({
          unit: 'px',
          x: (img.width - cropWidth) / 2,
          y: (img.height - cropHeight) / 2,
          width: cropWidth,
          height: cropHeight
        });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    
    // Calculate initial crop maintaining 4:5 aspect ratio
    const cropHeight = Math.min(height * 0.8, width * 0.8 / ASPECT_RATIO);
    const cropWidth = cropHeight * ASPECT_RATIO;
    
    const newCrop = {
      unit: 'px',
      x: (width - cropWidth) / 2,
      y: (height - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight
    };
    
    setCrop(newCrop);
    setCompletedCrop(newCrop);
  }, []);

  const getCroppedImage = useCallback(() => {
    if (!imgRef.current || !completedCrop) return null;

    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

    // Set output dimensions for portrait
    canvas.width = OUTPUT_WIDTH;
    canvas.height = OUTPUT_HEIGHT;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Fill with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw cropped image
    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      OUTPUT_WIDTH,
      OUTPUT_HEIGHT
    );

    return canvas.toDataURL('image/jpeg', 0.9);
  }, [completedCrop]);

  const handleCropComplete = () => {
    const croppedDataUrl = getCroppedImage();
    if (croppedDataUrl) {
      setPreviewUrl(croppedDataUrl);
      setStep('preview');
    } else {
      setError('Failed to generate preview. Please try again.');
    }
  };

  const handleUpload = async () => {
    if (!previewUrl) return;

    setUploading(true);
    setError('');

    try {
      await onUpload(previewUrl);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleBackToCrop = () => {
    setPreviewUrl(null);
    setStep('crop');
  };

  const resetSelection = () => {
    setSelectedFile(null);
    setImageSrc(null);
    setCrop(null);
    setCompletedCrop(null);
    setPreviewUrl(null);
    setStep('select');
    setError('');
    setScale(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {step === 'select' && 'Upload Profile Picture'}
            {step === 'crop' && 'Select Your Face Area'}
            {step === 'preview' && 'Preview Your Photo'}
          </h2>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600 p-1"
            disabled={uploading}
          >
            <MdClose className="text-2xl" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start gap-2">
              <MdWarning className="text-lg flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Step 1: Select Image */}
          {step === 'select' && (
            <div className="text-center py-8">
              {/* Current Picture Preview */}
              {currentPictureUrl && (
                <div className="mb-6">
                  <p className="text-sm text-gray-500 mb-2">Current picture:</p>
                  <img 
                    src={currentPictureUrl} 
                    alt="Current profile" 
                    className="w-32 h-32 object-cover rounded-lg mx-auto border border-gray-200"
                  />
                </div>
              )}

              <div 
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 hover:border-primary transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <MdImage className="text-6xl text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  Click to select an image
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  JPG, PNG, or WebP • Max {MAX_FILE_SIZE / 1024 / 1024}MB • Min {MIN_DIMENSION}x{MIN_DIMENSION}px
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="profile-picture-input"
                />
                <button className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors">
                  <MdUpload className="inline mr-2" />
                  Choose File
                </button>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg text-left">
                <h4 className="font-medium text-blue-800 mb-2">Tips for a great profile picture:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Use a recent photo with good lighting</li>
                  <li>• Face the camera directly</li>
                  <li>• Keep the background simple</li>
                  <li>• Your face should take up most of the frame</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 2: Crop Image */}
          {step === 'crop' && imageSrc && (
            <div>
              <p className="text-sm text-gray-600 mb-4 text-center">
                Drag to position the crop area over your face. The portrait frame will be used for your profile.
              </p>
              
              {/* Zoom Controls */}
              <div className="flex items-center justify-center gap-4 mb-4">
                <button
                  onClick={() => setScale(Math.max(0.5, scale - 0.1))}
                  className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                  title="Zoom out"
                >
                  <MdZoomOut className="text-xl" />
                </button>
                <span className="text-sm text-gray-600">{Math.round(scale * 100)}%</span>
                <button
                  onClick={() => setScale(Math.min(2, scale + 0.1))}
                  className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                  title="Zoom in"
                >
                  <MdZoomIn className="text-xl" />
                </button>
              </div>

              <div className="flex justify-center bg-gray-100 rounded-lg p-4 overflow-auto max-h-[400px]">
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={ASPECT_RATIO}
                  minWidth={100}
                  minHeight={100}
                >
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    alt="Crop preview"
                    onLoad={onImageLoad}
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '400px',
                      transform: `scale(${scale})`,
                      transformOrigin: 'center'
                    }}
                  />
                </ReactCrop>
              </div>

              <p className="text-xs text-gray-500 text-center mt-2">
                Output size: {OUTPUT_WIDTH}x{OUTPUT_HEIGHT}px (Square)
              </p>
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && previewUrl && (
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                This is how your profile picture will appear:
              </p>
              
              <div className="flex justify-center gap-8 items-start">
                {/* Portrait Preview */}
                <div>
                  <p className="text-xs text-gray-500 mb-2">Full Profile View</p>
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-48 h-48 object-cover rounded-lg shadow-lg border border-gray-200"
                  />
                </div>
                
                {/* Thumbnail Preview */}
                <div>
                  <p className="text-xs text-gray-500 mb-2">Thumbnail View</p>
                  <img
                    src={previewUrl}
                    alt="Thumbnail preview"
                    className="w-16 h-16 object-cover rounded-full shadow border border-gray-200"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-200 flex justify-between">
          {step === 'select' && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <div></div>
            </>
          )}

          {step === 'crop' && (
            <>
              <button
                onClick={resetSelection}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                ← Change Image
              </button>
              <button
                onClick={handleCropComplete}
                disabled={!completedCrop}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <MdCrop />
                Crop & Preview
              </button>
            </>
          )}

          {step === 'preview' && (
            <>
              <button
                onClick={handleBackToCrop}
                disabled={uploading}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                ← Adjust Crop
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="upload-picture-btn"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <MdCheck />
                    Save Picture
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePictureCropper;
